import {
    ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef,
    Input, OnDestroy, OnInit, ViewChild,
} from "@angular/core";
import { FormControl } from "@angular/forms";
import { BehaviorSubject, combineLatest, Subject } from "rxjs";
import { debounceTime, startWith, takeUntil } from "rxjs/operators";
import { GanttCompiledData, GanttComponentData, GanttDataset, ResolvedTypography } from "@core/data/types/advanced-report";
import { applyChartTypography } from "@core/fonts/chart-typography";
import Chart from 'chart.js/auto';

/**
 * Rendering child for the Gantt component: owns the Chart.js lifecycle and re-renders
 * reactively when the saved config (`ganttControl`) or the compiled payload changes.
 */
@Component({
    selector: 'pv-report-gantt-graph',
    templateUrl: 'report-gantt-graph.html',
    styleUrls:  ['report-gantt-graph.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    preserveWhitespaces: false,
})
export class ReportGanttGraph implements OnInit, OnDestroy {

    @ViewChild('ganttCanvas') canvasRef: ElementRef<HTMLCanvasElement>;

    @Input() ganttControl: FormControl;

    @Input()
    set compiledData(data: GanttCompiledData) {
        if (this._compiledData$.getValue() !== data) this._compiledData$.next(data);
    }

    @Input()
    set typography(value: ResolvedTypography) {
        this._typography = value;
        // Re-emit so the chart rebuilds with the new font.
        this._compiledData$.next(this._compiledData$.getValue());
    }
    private _typography: ResolvedTypography;

    /** Screen-reader summary; the canvas is opaque to assistive tech. */
    get ariaLabel(): string {
        const data = this._compiledData$.getValue();
        const datasets = data?.datasets ?? [];
        const hasData = datasets.some(ds => (ds.data?.length ?? 0) > 0);
        if (!hasData) return 'Gantt chart with no data for the selected date characteristics';
        const rows = data?.labels?.length ?? 0;
        const base = `Gantt chart showing ${rows} row${rows === 1 ? '' : 's'}`;
        const suppressed = this.suppressedCount;
        return suppressed
            ? `${base}. ${suppressed} row${suppressed === 1 ? '' : 's'} could not be drawn on the current axis and are listed without a bar.`
            : base;
    }

    /** Rows whose span wraps the chosen axis start — fixable by switching it. */
    get wrapsAxisCount(): number {
        return this._countFlag(ds => ds.wrapsAxis);
    }

    /** Rows aggregating multiple seasons — can't be drawn on any axis. */
    get multiSeasonCount(): number {
        return this._countFlag(ds => ds.multiSeason);
    }

    get suppressedCount(): number {
        return this.wrapsAxisCount + this.multiSeasonCount;
    }

    private _countFlag(pick: (ds: GanttDataset) => boolean[] | undefined): number {
        const datasets = this._compiledData$.getValue()?.datasets ?? [];
        return datasets.reduce((n, ds) => n + (pick(ds) ?? []).filter(Boolean).length, 0);
    }

    /** Per-row OR of the suppression flags; marks suppressed rows with a y-axis asterisk. */
    flaggedRows(compiled: GanttCompiledData): boolean[] {
        const rows = compiled?.labels?.length ?? 0;
        const flags = new Array<boolean>(rows).fill(false);
        (compiled?.datasets ?? []).forEach(ds => {
            (ds.wrapsAxis ?? []).forEach((f, i) => { if (f) flags[i] = true; });
            (ds.multiSeason ?? []).forEach((f, i) => { if (f) flags[i] = true; });
        });
        return flags;
    }

    private _chart: Chart | null = null;
    private _compiledData$ = new BehaviorSubject<GanttCompiledData>(null);
    private _destroy$ = new Subject<void>();

    constructor(private _changes: ChangeDetectorRef) {}

    ngOnInit() {
        // startWith primes the first paint; takeUntil is last so a debounced emit can't
        // fire into a torn-down canvas.
        combineLatest(
            this.ganttControl.valueChanges.pipe(startWith(this.ganttControl.value)),
            this._compiledData$,
        ).pipe(
            debounceTime(100),
            takeUntil(this._destroy$),
        ).subscribe(([cfg, compiled]) =>
            this._render(compiled as GanttCompiledData, cfg as GanttComponentData));
    }

    ngOnDestroy() {
        this._destroyChart();
        this._destroy$.next();
        this._destroy$.complete();
    }

    private _render(compiled: GanttCompiledData, cfg: GanttComponentData) {
        this._destroyChart();
        const canvas = this.canvasRef?.nativeElement;
        if (!canvas || !compiled?.datasets?.length) return;

        try {
            const config = this.buildConfig(compiled, cfg ?? ({} as GanttComponentData));
            // Family only — keeps the Gantt's tuned axis/title sizes.
            applyChartTypography(config.options, this._typography);
            this._chart = new Chart(canvas, config);
            this._changes.markForCheck();
        } catch (error) {
            console.error('ReportGanttGraph: error rendering Gantt chart', error);
            this._destroyChart();
        }
    }

    /** Axis range in rotated slots. 'full' = all 53; 'zoomed' = padded data range, floored/ceiled to whole weeks. */
    resolveAxis(compiled: GanttCompiledData, cfg: GanttComponentData): { axisMin: number; axisMax: number } {
        const viewMode = cfg?.viewMode ?? 'zoomed';
        const axisMin = viewMode === 'full' ? 1  : Math.floor(compiled.axisMin ?? 1);
        const axisMax = viewMode === 'full' ? 53 : Math.ceil(compiled.axisMax ?? 52);
        return { axisMin, axisMax };
    }

    /** Builds the Chart.js config. Returns `any` to match the original inline literal passed to `new Chart()`. */
    buildConfig(compiled: GanttCompiledData, cfg: GanttComponentData): any {
        const title = (cfg?.title ?? '').trim();
        const axisStartWeek = cfg?.axisStartWeek ?? compiled?.axisStartWeek ?? 1;
        const { axisMin, axisMax } = this.resolveAxis(compiled, cfg);
        const flagged = this.flaggedRows(compiled);

        // Reverse-map a rotated slot to its true ISO week for human-facing labels.
        const toWeek = (slot: number) => ((slot - 1 + axisStartWeek - 1) % 53) + 1;
        const wLabel = (slot: number) => `W${String(toWeek(slot)).padStart(2, '0')}`;

        return {
            type: 'bar',
            data: {
                labels:   compiled.labels.map((l, i) => flagged[i] ? `${l} *` : l),
                datasets: compiled.datasets.map(ds => ({
                    ...ds,
                    // Server sends null for suppressed rows; the normalize guards a reversed pair.
                    data: (ds.data ?? []).map(
                        d => d == null
                            ? null
                            : (d[0] <= d[1] ? [d[0], d[1]] : [d[1], d[0]]) as [number, number],
                    ),
                    // Pixel floor so a single-date (zero-width) milestone stays visible.
                    minBarLength: 8,
                    borderSkipped: false,
                    maxBarThickness: 28,
                    categoryPercentage: 0.7,
                    barPercentage: 0.9,
                } as any)),
            },
            options: {
                indexAxis: 'y',
                responsive: true,
                maintainAspectRatio: false,
                animation: false,
                layout: { padding: { top: 4, right: 12, bottom: 4, left: 4 } },
                plugins: {
                    legend: { display: false },
                    title: {
                        display: !!title,
                        text: title,
                        align: 'center',
                        position: 'top',
                        color: '#37474F',
                        font: { size: 14, weight: '600' },
                        padding: { top: 2, bottom: 8 },
                    },
                    tooltip: {
                        backgroundColor: 'rgba(30,30,30,0.92)',
                        titleFont: { size: 12, weight: 'bold' },
                        bodyFont: { size: 11 },
                        padding: 10,
                        cornerRadius: 6,
                        callbacks: {
                            title: (items) => items[0]?.label ?? '',
                            label: (item) => {
                                const ds    = item.dataset as any;
                                const lines: string[] = ds.charLabels?.[item.dataIndex] ?? [];
                                // raw is in slot coords — show true weeks.
                                const [s, e] = item.raw as [number, number];
                                const rangeLabel = s === e
                                    ? `Week: ${wLabel(s)}`
                                    : `Range: ${wLabel(s)} → ${wLabel(e)}`;
                                return [...lines, rangeLabel];
                            },
                        },
                    },
                },
                scales: {
                    x: {
                        type: 'linear',
                        min: axisMin,
                        max: axisMax,
                        beginAtZero: false,
                        grid: { color: 'rgba(0,0,0,0.05)', drawBorder: false },
                        ticks: {
                            stepSize: 1,
                            autoSkip: true,
                            maxRotation: 0,
                            minRotation: 0,
                            font: { size: 10 },
                            color: '#666',
                            // Reverse-map slot → week; drop fractional ticks.
                            callback: (value: number) => Number.isInteger(value) ? wLabel(value) : '',
                        },
                    },
                    y: {
                        grid: { display: false },
                        ticks: { font: { size: 11 }, color: '#444' },
                    },
                },
            },
        };
    }

    private _destroyChart() {
        if (this._chart) { this._chart.destroy(); this._chart = null; }
    }
}
